/**
 * Which group membership calls a camera save should issue (#152).
 *
 * The camera edit sheet's group picker is single-valued, so changing it means
 * "remove from the old group, add to the new one". Group membership is a
 * many-to-many across owners, though, and a superuser may now write to groups
 * they do not own — so an unguarded remove is a silent cross-account deletion.
 * The rule: only ever remove a camera from a group the requester OWNS.
 */
export interface GroupSaveOps {
  /** Group to remove the camera from, or null. */
  remove: number | null
  /** Group to add the camera to, or null. */
  add: number | null
}

export function planGroupSaveOps(
  previousGroupId: number | null,
  newGroupId: number | null,
  groups: ReadonlyArray<{ id: number; is_owner?: boolean }>,
): GroupSaveOps {
  if (previousGroupId === newGroupId) return { remove: null, add: null }

  // `is_owner` is undefined only on a group the requester cannot see; treat that
  // as not-owned, because removal is the irreversible direction.
  const previousGroup = groups.find((g) => g.id === previousGroupId)
  const ownsPrevious = previousGroup?.is_owner === true

  return {
    remove: previousGroupId && ownsPrevious ? previousGroupId : null,
    add: newGroupId ?? null,
  }
}
